import { useState, useMemo, useEffect } from "react";
import BaseModal from "./BaseModal";
import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import CheckBox from "@components/ui/selection/CheckBox";
import { useDataStore } from "@store/user_org";
import type { DevisChannel, DevisDelivery, DevisSendTargets } from "types/estimateExtraction";
import { describeUnpricedParts, type UnpricedPart } from "@utils/unpricedParts";

type Reminder = {
  id: string;
  type: string;
  scheduled_at: Date;
  enabled: boolean;
  skipReason?: string;
  /** 1er devis : à l'envoi de ce rappel (le dernier), alerte au chargé de dossier. */
  alertTeam?: boolean;
};

type Props = {
  onClose: () => void;
  /** `channels` absent = aperçu indisponible, le backend envoie partout où il peut. */
  onConfirm: (
    remindersEnabled: boolean,
    disabledReminders: string[],
    channels?: DevisChannel[],
    allowUnpricedParts?: boolean,
  ) => void;
  receptionDate: Date | null;
  /** Destinataires résolus par le backend ; null tant qu'ils se chargent. */
  targets: DevisSendTargets | null;
  loading?: boolean;
  /** "initial" (1er devis) annule le RDV sans réponse ; "complementaire" alerte l'équipe. */
  devisKind?: "initial" | "complementaire";
  /** Pièces à 0,00 € des postes envoyés : envoi bloqué tant que le garage ne confirme pas. */
  unpricedParts?: UnpricedPart[];
};

const HOUR = 3600000;

/** Pourquoi WhatsApp ne part pas — raisons du backend (résolution ET envoi). */
const WHATSAPP_REASON_LABELS: Record<string, string> = {
  no_phone: "aucun numéro de mobile sur la fiche client",
  not_mobile: "le numéro enregistré est une ligne fixe",
  no_discussion: "pas de conversation client sur cet ordre",
  whatsapp_not_configured: "WhatsApp n'est pas configuré pour le garage",
  client_muted: "client en sourdine",
  insufficient_balance: "solde de communications insuffisant",
  template_not_approved: "modèle WhatsApp non approuvé par Meta",
  no_template_outside_window: "hors fenêtre de 24 h, sans modèle",
  twilio_sender_not_configured: "numéro WhatsApp du garage non configuré",
  unknown: "coordonnées non vérifiées",
};

export function whatsappReasonLabel(reason: string | null | undefined): string {
  return (reason && WHATSAPP_REASON_LABELS[reason]) || "échec de l'envoi";
}

type DeliveryLine = { channel: string; sent: boolean; attempted: boolean; text: string };

/**
 * Bilan d'un envoi de devis, canal par canal, lisible par le garage. `ok` = au moins
 * un canal a abouti ; `attempted` distingue un échec d'un canal simplement absent.
 */
export function describeDelivery(delivery: DevisDelivery | undefined): {
  ok: boolean;
  lines: DeliveryLine[];
} {
  if (!delivery) return { ok: true, lines: [] };
  const lines: DeliveryLine[] = [];
  const { email, whatsapp } = delivery;
  if (email.requested === false) {
    lines.push({ channel: "E-mail", sent: false, attempted: false, text: "non sélectionné" });
  } else if (email.to) {
    lines.push({
      channel: "E-mail",
      sent: email.sent,
      attempted: true,
      text: email.sent
        ? `envoyé à ${email.to}`
        : `échec vers ${email.to}${email.error === "client_muted" ? " (client en sourdine)" : ""}`,
    });
  } else {
    lines.push({ channel: "E-mail", sent: false, attempted: false, text: "non envoyé (aucune adresse)" });
  }
  if (whatsapp.requested === false) {
    lines.push({ channel: "WhatsApp", sent: false, attempted: false, text: "non sélectionné" });
  } else if (whatsapp.sent) {
    lines.push({ channel: "WhatsApp", sent: true, attempted: true, text: `envoyé au ${whatsapp.to}` });
  } else {
    lines.push({
      channel: "WhatsApp",
      sent: false,
      attempted: !!whatsapp.to,
      text: whatsapp.to
        ? `échec vers ${whatsapp.to} (${whatsappReasonLabel(whatsapp.error)})`
        : `non envoyé (${whatsappReasonLabel(whatsapp.error)})`,
    });
  }
  return { ok: email.sent || whatsapp.sent, lines };
}

type PreviewConfig = {
  r1_delay_hours: number;
  r2_delay_hours: number;
  r3_before_reception_hours: number;
  cancel_before_reception_hours: number;
  min_gap_hours: number;
  short_notice_delay_hours: number;
  short_notice_min_lead_hours: number;
  quiet_end_hour: number | null;
  quiet_start_hour: number | null;
  /** 1er devis : plus d'annulation automatique par défaut (`initial_auto_cancel`). */
  auto_cancel: boolean;
};

/** Repousse un envoi hors de la plage silencieuse, sans jamais franchir son échéance. */
function shiftOutOfQuietHours(
  d: Date,
  openHour: number | null,
  closeHour: number | null,
  limit?: Date | null,
): Date {
  if (openHour === null || closeHour === null) return d;
  const shifted = new Date(d);
  if (d.getHours() < openHour) {
    shifted.setHours(openHour, 0, 0, 0);
  } else if (d.getHours() >= closeHour) {
    shifted.setDate(shifted.getDate() + 1);
    shifted.setHours(openHour, 0, 0, 0);
  } else {
    return d;
  }
  if (limit && shifted.getTime() >= limit.getTime()) return d;
  return shifted;
}

/**
 * Miroir de `compute_reminder_schedule` (app/services/devis_reminder_service.py).
 *
 * Le garage décide d'envoyer le devis sur la foi de cet aperçu : il doit montrer le
 * calendrier réel, rappels abandonnés compris — et pourquoi ils le sont. Toute
 * évolution des règles côté backend se reporte ici.
 */
function computePreviewSchedule(
  sendTime: Date,
  receptionDt: Date,
  config: PreviewConfig,
): Reminder[] {
  const quiet = (d: Date, limit?: Date | null) =>
    shiftOutOfQuietHours(d, config.quiet_end_hour, config.quiet_start_hour, limit);

  const hoursUntilReception = (receptionDt.getTime() - sendTime.getTime()) / HOUR;

  // L'annulation, échéance autour de laquelle tout le reste se place.
  let cancelAt: Date | null = null;
  const rawCancel = new Date(
    receptionDt.getTime() - config.cancel_before_reception_hours * HOUR,
  );
  if (config.auto_cancel && rawCancel.getTime() > sendTime.getTime())
    cancelAt = quiet(rawCancel, receptionDt);

  const deadline = cancelAt ?? receptionDt;

  // Le rappel critique : R3, ou son repli si le devis part après l'heure de R3.
  let urgent: Reminder | null = null;
  const r3Time = new Date(receptionDt.getTime() - config.r3_before_reception_hours * HOUR);
  if (
    r3Time.getTime() > sendTime.getTime() &&
    (!cancelAt || r3Time.getTime() < cancelAt.getTime())
  ) {
    urgent = {
      id: "r3",
      type: "reminder_last",
      scheduled_at: quiet(r3Time, deadline),
      enabled: true,
    };
  } else {
    let latest = new Date(
      receptionDt.getTime() - config.short_notice_min_lead_hours * HOUR,
    );
    if (cancelAt && cancelAt.getTime() - HOUR < latest.getTime()) {
      latest = new Date(cancelAt.getTime() - HOUR);
    }
    const at = new Date(
      Math.min(sendTime.getTime() + config.short_notice_delay_hours * HOUR, latest.getTime()),
    );
    if (at.getTime() > sendTime.getTime()) {
      urgent = {
        id: "r0",
        type: "reminder_short_notice",
        scheduled_at: quiet(at, latest),
        enabled: true,
      };
    }
  }

  const schedule: Reminder[] = [];
  const taken: Date[] = [];
  if (urgent) {
    schedule.push(urgent);
    taken.push(urgent.scheduled_at);
  }
  if (cancelAt) taken.push(cancelAt);

  const minGap = config.min_gap_hours * HOUR;
  const droppable =
    hoursUntilReception > 48
      ? [
          {
            id: "r1",
            type: "reminder_24h",
            at: new Date(sendTime.getTime() + config.r1_delay_hours * HOUR),
          },
          {
            id: "r2",
            type: "reminder_48h",
            at: new Date(sendTime.getTime() + config.r2_delay_hours * HOUR),
          },
        ]
      : [];

  for (const candidate of droppable) {
    const at = quiet(candidate.at, deadline);
    const entry: Reminder = {
      id: candidate.id,
      type: candidate.type,
      scheduled_at: at,
      enabled: true,
    };
    if (at.getTime() >= deadline.getTime()) {
      schedule.push({ ...entry, enabled: false, skipReason: cancelAt ? "après l'annulation" : "après le RDV" });
    } else if (taken.some((t) => Math.abs(at.getTime() - t.getTime()) < minGap)) {
      schedule.push({ ...entry, enabled: false, skipReason: "écart trop court" });
    } else {
      schedule.push(entry);
      taken.push(at);
    }
  }

  schedule.sort((a, b) => a.scheduled_at.getTime() - b.scheduled_at.getTime());
  if (cancelAt) {
    schedule.push({
      id: "cancel",
      type: "auto_cancel",
      scheduled_at: cancelAt,
      enabled: true,
    });
  }
  return schedule;
}

/**
 * Miroir de `compute_fixed_schedule` (app/services/devis_reminder_service.py) :
 * - 1er devis : rappels à heure fixe J+2 et J+3, alerte au chargé de dossier À L'ENVOI
 *   du dernier (16/10) ; annulation à 16h le même jour seulement si `initial_auto_cancel`
 *   (désactivée par défaut depuis le 15/10 : le dernier rappel prévient que le RDV risque
 *   d'être annulé) ;
 * - devis complémentaire : rappels J+1, J+2, J+3, alerte à l'équipe deux heures après.
 * Rend null quand un 1er devis (annulation, ou à défaut dernier rappel) ne tient pas avant le RDV : le backend repasse alors au
 * calendrier adaptatif.
 */
function computeFixedPreview(
  sendTime: Date,
  receptionDt: Date | null,
  hour: number,
  finalDelayHours: number,
  minLeadHours: number,
  kind: "initial" | "complementaire",
  initialDays: number[] = [2, 3],
  cancelHour = 16,
  autoCancel = false,
): Reminder[] | null {
  const dayAt = (n: number, h = hour) => {
    const d = new Date(sendTime);
    d.setDate(d.getDate() + n);
    d.setHours(h, 0, 0, 0);
    return d;
  };
  if (kind === "complementaire") {
    return [
      { id: "r1", type: "reminder_24h", scheduled_at: dayAt(1), enabled: true },
      { id: "r2", type: "reminder_48h", scheduled_at: dayAt(2), enabled: true },
      { id: "r3", type: "reminder_last", scheduled_at: dayAt(3), enabled: true },
      {
        id: "followup",
        type: "manual_followup",
        scheduled_at: new Date(dayAt(3).getTime() + finalDelayHours * HOUR),
        enabled: true,
      },
    ];
  }
  const lastDay = initialDays[initialDays.length - 1];
  const cancelAt = dayAt(lastDay, cancelHour);
  const cancel = receptionDt !== null && autoCancel;
  const mustPrecede = cancel ? cancelAt : dayAt(lastDay);
  if (receptionDt && mustPrecede.getTime() > receptionDt.getTime() - minLeadHours * HOUR) return null;

  const schedule: Reminder[] = initialDays.map((n, i) => ({
    id: `r${i + 1}`,
    type: n === lastDay ? "reminder_last" : i === 0 ? "reminder_24h" : "reminder_48h",
    scheduled_at: dayAt(n),
    enabled: true,
  }));
  if (cancel) {
    schedule.push({ id: "cancel", type: "auto_cancel", scheduled_at: cancelAt, enabled: true });
  }
  return schedule;
}

const LABEL_MAP: Record<string, string> = {
  r1: "Rappel 1 (24h)",
  r2: "Rappel 2 (48h)",
  r3: "Dernier rappel",
  r0: "Rappel (délai court)",
  cancel: "Annulation automatique",
  followup: "Alerte équipe : reprise manuelle",
};

/** Miroir de `mark_team_alert` : le dernier rappel encore actif d'un 1er devis avec RDV
 *  déclenche, à son envoi, l'alerte au chargé de dossier (« 2e relance envoyée »). */
function markTeamAlert(schedule: Reminder[], disabledIds: Set<string>): Reminder[] {
  const reminders = schedule.filter((r) => r.id.startsWith("r") && r.enabled && !disabledIds.has(r.id));
  const last = reminders[reminders.length - 1];
  return schedule.map((r) => ({ ...r, alertTeam: !!last && r.id === last.id }));
}

const FIXED_LABEL_MAP: Record<"initial" | "complementaire", Record<string, string>> = {
  initial: {
    r1: "Rappel 1 (J+2)",
    r2: "Dernier rappel (J+3)",
  },
  complementaire: {
    r1: "Rappel 1 (J+1)",
    r2: "Rappel 2 (J+2)",
    r3: "Dernier rappel (J+3)",
  },
};

function formatDateTime(d: Date): string {
  return d.toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }) + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

export default function DevisSendPreviewModal({
  onClose,
  onConfirm,
  receptionDate,
  targets,
  loading,
  devisKind = "initial",
  unpricedParts = [],
}: Props) {
  const orgData = useDataStore((s) => s.orgData);
  const reminderConfig = orgData?.config?.devis_reminders || {};

  const config = {
    r1_delay_hours: reminderConfig.r1_delay_hours ?? 24,
    r2_delay_hours: reminderConfig.r2_delay_hours ?? 48,
    r3_before_reception_hours: reminderConfig.r3_before_reception_hours ?? 48,
    cancel_before_reception_hours: reminderConfig.cancel_before_reception_hours ?? 42,
    min_gap_hours: reminderConfig.min_gap_hours ?? 12,
    short_notice_delay_hours: reminderConfig.short_notice_delay_hours ?? 4,
    short_notice_min_lead_hours: reminderConfig.short_notice_min_lead_hours ?? 2,
    quiet_end_hour: reminderConfig.quiet_end_hour ?? 8,
    quiet_start_hour: reminderConfig.quiet_start_hour ?? 20,
    // Comme le backend : seul le 1er devis perd l'annulation automatique.
    auto_cancel: devisKind === "complementaire" || reminderConfig.initial_auto_cancel === true,
  };

  const orgEnabled = reminderConfig.enabled !== false;

  const fixedMode = reminderConfig.fixed_schedule !== false;

  const { schedule, isFixed } = useMemo(() => {
    const now = new Date();
    if (fixedMode) {
      const fixed = computeFixedPreview(
        now,
        receptionDate,
        reminderConfig.fixed_hour ?? 12,
        reminderConfig.final_action_delay_hours ?? 2,
        config.short_notice_min_lead_hours,
        devisKind,
        reminderConfig.initial_reminder_days ?? [2, 3],
        reminderConfig.cancel_hour ?? 16,
        reminderConfig.initial_auto_cancel === true,
      );
      if (fixed) return { schedule: fixed, isFixed: true };
    }
    if (!receptionDate) return { schedule: [] as Reminder[], isFixed: false };
    return { schedule: computePreviewSchedule(now, receptionDate, config), isFixed: false };
  }, [receptionDate, config, fixedMode, devisKind]);

  const [globalEnabled, setGlobalEnabled] = useState(orgEnabled);
  const [disabledIds, setDisabledIds] = useState<Set<string>>(new Set());

  const toggleReminder = (id: string) => {
    if (id === "cancel" || id === "followup") return;
    setDisabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Ni e-mail ni WhatsApp : l'envoi n'atteindrait personne, le backend le refuse.
  const noTarget =
    !!targets && !targets.email && !targets.whatsapp_phone && targets.whatsapp_reason !== "unknown";

  // Mode d'envoi choisi par le garage (demande Héric, 28/09). Par défaut WhatsApp seul
  // (demande Héric, 29/09) : l'e-mail se coche à la main — sauf sans WhatsApp possible,
  // où il reste coché pour que le devis parte. Aperçu indisponible (« unknown ») : pas
  // de choix, le backend envoie partout où il peut.
  const canChoose = !!targets && targets.whatsapp_reason !== "unknown";
  const emailAvailable = !!targets?.email;
  const whatsappAvailable = !!targets?.whatsapp_phone;
  const [emailOn, setEmailOn] = useState(false);
  const [whatsappOn, setWhatsappOn] = useState(false);
  useEffect(() => {
    setEmailOn(!!targets?.email && !targets?.whatsapp_phone);
    setWhatsappOn(!!targets?.whatsapp_phone);
  }, [targets]);
  const nothingSelected = canChoose && !noTarget && !emailOn && !whatsappOn;
  // Ordre importé : jamais de relance WhatsApp. En « WhatsApp seul », aucun rappel ne partirait.
  const whatsappOnlyWithoutReminders =
    canChoose &&
    whatsappOn &&
    !emailOn &&
    globalEnabled &&
    targets?.whatsapp_reminders_allowed === false;

  // Pièces non valorisées (OR-1187373) : bloqué sauf confirmation explicite, gardée sur l'envoi.
  const hasUnpriced = unpricedParts.length > 0;
  const [unpricedConfirmed, setUnpricedConfirmed] = useState(false);
  const unpricedBlocked = hasUnpriced && !unpricedConfirmed;

  const handleConfirm = () => {
    if (!targets || noTarget || nothingSelected || unpricedBlocked) return;
    const channels: DevisChannel[] | undefined = canChoose
      ? [
          ...(emailOn && emailAvailable ? (["email"] as DevisChannel[]) : []),
          ...(whatsappOn && whatsappAvailable ? (["whatsapp"] as DevisChannel[]) : []),
        ]
      : undefined;
    onConfirm(globalEnabled, Array.from(disabledIds), channels, hasUnpriced && unpricedConfirmed);
  };

  return (
    <BaseModal title="Envoyer le devis au client" onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-4 p-4">
        {hasUnpriced && (
          <div className="flex flex-col gap-2 rounded bg-red-50 text-red-700 px-3 py-2 text-sm">
            <div className="flex items-start gap-2">
              <Icon1 name="warning" size={14} />
              <span>
                Le devis contient des pièces à 0,00 € (non valorisées dans ServiceBox/Alpha) :{" "}
                <strong>{describeUnpricedParts(unpricedParts)}</strong>. Récupérez à nouveau le
                devis une fois les prix remontés, ou confirmez que ces pièces sont offertes.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckBox
                checked={unpricedConfirmed}
                onChange={() => setUnpricedConfirmed(!unpricedConfirmed)}
              />
              <span
                className="cursor-pointer"
                onClick={() => setUnpricedConfirmed(!unpricedConfirmed)}
              >
                Ces pièces sont volontairement à 0 €
              </span>
            </div>
          </div>
        )}

        {/* Par où part le devis : e-mail et WhatsApp, tels que l'envoi les résoudra. */}
        <div className="flex flex-col gap-1 text-sm text-gray-600">
          <span className="font-medium text-gray-800">Le devis sera envoyé :</span>
          {!targets ? (
            <span className="text-gray-400">Vérification des coordonnées du client…</span>
          ) : (
            <>
              <div className="flex items-center gap-2">
                {canChoose && (
                  <CheckBox
                    checked={emailAvailable && emailOn}
                    disabled={!emailAvailable}
                    onChange={() => emailAvailable && setEmailOn(!emailOn)}
                  />
                )}
                <span
                  className={emailAvailable && canChoose ? "cursor-pointer" : ""}
                  onClick={() => canChoose && emailAvailable && setEmailOn(!emailOn)}
                >
                  Par e-mail :{" "}
                  {targets.email ? (
                    <strong className={canChoose && !emailOn ? "text-gray-400 line-through" : ""}>
                      {targets.email}
                    </strong>
                  ) : (
                    <span className="text-gray-400">non (aucune adresse)</span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {canChoose && (
                  <CheckBox
                    checked={whatsappAvailable && whatsappOn}
                    disabled={!whatsappAvailable}
                    onChange={() => whatsappAvailable && setWhatsappOn(!whatsappOn)}
                  />
                )}
                <span
                  className={whatsappAvailable && canChoose ? "cursor-pointer" : ""}
                  onClick={() => canChoose && whatsappAvailable && setWhatsappOn(!whatsappOn)}
                >
                  Par WhatsApp :{" "}
                  {targets.whatsapp_phone ? (
                    <strong className={canChoose && !whatsappOn ? "text-gray-400 line-through" : ""}>
                      {targets.whatsapp_phone}
                    </strong>
                  ) : (
                    <span className="text-gray-400">
                      non ({whatsappReasonLabel(targets.whatsapp_reason)})
                    </span>
                  )}
                </span>
              </div>
            </>
          )}
          {nothingSelected && (
            <div className="flex items-start gap-2 rounded bg-amber-50 text-amber-800 px-3 py-2 mt-1">
              <Icon1 name="warning" size={14} />
              <span>Cochez au moins un mode d'envoi.</span>
            </div>
          )}
          {whatsappOnlyWithoutReminders && (
            <div className="flex items-start gap-2 rounded bg-amber-50 text-amber-800 px-3 py-2 mt-1">
              <Icon1 name="warning" size={14} />
              <span>
                Ordre importé : les rappels automatiques ne partent jamais par WhatsApp.
                Sans l'e-mail, le client ne recevra aucun rappel.
              </span>
            </div>
          )}
          {noTarget && (
            <div className="flex items-start gap-2 rounded bg-red-50 text-red-700 px-3 py-2 mt-1">
              <Icon1 name="warning" size={14} />
              <span>
                Aucune coordonnée pour joindre le client : renseignez son e-mail ou son
                mobile sur la fiche client avant d'envoyer le devis.
              </span>
            </div>
          )}
        </div>

        {(receptionDate || schedule.length > 0) && (
          <>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">Rappels planifiés</span>
                <div className="flex items-center gap-2">
                  <CheckBox
                    checked={globalEnabled}
                    onChange={() => setGlobalEnabled(!globalEnabled)}
                  />
                  <span className="text-h3 cursor-pointer" onClick={() => setGlobalEnabled(!globalEnabled)}>Activer les rappels</span>
                </div>
              </div>

              {globalEnabled && schedule.length > 0 && (
                <div className="flex flex-col gap-2">
                  {(devisKind === "initial" && receptionDate
                    ? markTeamAlert(schedule, disabledIds)
                    : schedule
                  ).map((r) => {
                    const isCancel = r.id === "cancel" || r.id === "followup";
                    const isSkipped = !r.enabled;
                    const isUserDisabled = disabledIds.has(r.id);
                    const active = r.enabled && !isUserDisabled;

                    return (
                      <div
                        key={r.id}
                        className={`flex items-center gap-2 text-sm rounded px-3 py-2 ${
                          isCancel
                            ? "bg-red-50 text-red-700"
                            : active
                              ? "bg-gray-50"
                              : "bg-gray-50 text-gray-400"
                        }`}
                      >
                        {!isCancel && !isSkipped && (
                          <CheckBox
                            checked={!isUserDisabled}
                            onChange={() => toggleReminder(r.id)}
                          />
                        )}
                        {isCancel && <Icon1 name="warning" size={14} />}
                        {isSkipped && <span className="w-4" />}
                        <span className="flex-1">
                          {(isFixed && FIXED_LABEL_MAP[devisKind][r.id]) || LABEL_MAP[r.id] || r.id}
                          {isSkipped && ` (${r.skipReason || "écart trop court"})`}
                          {r.alertTeam && (
                            <span className="block text-xs text-gray-500">+ alerte au chargé de dossier à l'envoi</span>
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDateTime(r.scheduled_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {globalEnabled && schedule.length === 0 && (
                <p className="text-sm text-gray-400">
                  Aucun rappel planifiable (pas de date de réception)
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <SecondaryButton variant="small" label="Annuler" onClick={onClose} />
          <Button
            variant="small"
            label={loading ? "Envoi..." : "Envoyer"}
            onClick={handleConfirm}
            disabled={loading || !targets || noTarget || nothingSelected || unpricedBlocked}
          />
        </div>
      </div>
    </BaseModal>
  );
}
